import React from "react"
import { css, type Theme } from "@emotion/react"
import styled from "@emotion/styled"
import { Link, NavLink } from "react-router"
import { Medium14, Medium16Body } from "@myvp/shared/src/typography"
import {
  type CoreButtonProps,
  InternalButton,
  InternalLink,
  InternalNavLink,
  type InputButtonTextColors,
  activeCss,
  getButtonIconColor,
  heightStyles,
  variantStyles,
} from "@myvp/shared/src/components/buttons/button-utils"
import { type IconElement } from "@myvp/shared/src/styles/icon-utils"

export interface TextButtonProps extends CoreButtonProps {
  children: React.ReactNode
  startIcon?: IconElement
  endIcon?: IconElement
  textColor?: InputButtonTextColors
}

export interface ExternalButtonProps
  extends Omit<React.ComponentProps<"button">, "children">, TextButtonProps {
  internalType?: "button"
}
export interface ExternalButtonLinkProps
  extends Omit<React.ComponentProps<typeof Link>, "children">, TextButtonProps {
  internalType: "link"
}
export interface ExternalButtonNavLinkProps
  extends
    React.ComponentProps<typeof NavLink>,
    Omit<TextButtonProps, "children" | "className" | "style"> {
  internalType: "navlink"
}

const baseStyles = (theme: Theme) => css`
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  border-radius: 4px;
  padding: 4px;
  gap: 4px;
  cursor: pointer;

  :focus-visible {
    outline: 2px solid ${theme.palette.border.focus.default};
    outline-offset: 1px;
  }
`

const Button = (
  props:
    | ExternalButtonProps
    | ExternalButtonLinkProps
    | ExternalButtonNavLinkProps
) => {
  const disabled =
    "disabled" in props
      ? props.disabled
      : props["aria-disabled"] === true || props["aria-disabled"] === "true"
  const styles = [
    baseStyles,
    heightStyles[props.height],
    variantStyles[props.variant],
    activeCss(!!props.isAnchorOpen),
    (theme: Theme) =>
      props.variant === "tertiary" &&
      !disabled &&
      props.textColor &&
      css`
        color: ${props.textColor === "link"
          ? theme.palette.content.link.default
          : theme.palette.content.neutral[props.textColor]};
      `,
  ]
  const renderChildren = (children: React.ReactNode) => {
    const Typography = getButtonTypography(props)
    const color = getButtonIconColor({
      variant: props.variant,
      iconColor: props.textColor,
      disabled,
    })
    return (
      <>
        {props.startIcon?.({ size: "medium", color }) ?? null}
        {typeof children === "string" ? (
          <Typography
            css={css`
              text-align: center;
            `}
          >
            {children}
          </Typography>
        ) : (
          children
        )}

        {props.endIcon?.({ size: "medium", color }) ?? null}
      </>
    )
  }

  if (props.internalType === "navlink") {
    const navLinkProps = omitButtonProps(props)
    const children = navLinkProps.children
    return (
      <InternalNavLink {...navLinkProps} css={styles}>
        {typeof children === "function"
          ? (args) => renderChildren(children(args))
          : renderChildren(children)}
      </InternalNavLink>
    )
  } else if (props.internalType === "link") {
    const linkProps = omitButtonProps(props)
    return (
      <InternalLink {...linkProps} css={styles}>
        {renderChildren(linkProps.children)}
      </InternalLink>
    )
  } else {
    const buttonProps = omitButtonProps(props)
    return (
      <InternalButton {...buttonProps} css={styles}>
        {renderChildren(buttonProps.children)}
      </InternalButton>
    )
  }
}

export const getButtonTypography = (args: Pick<CoreButtonProps, "height">) =>
  args.height === "large" ? Medium16Body : Medium14

export default styled(Button)``

const omitButtonProps = <P extends React.ComponentProps<typeof Button>>(
  allProps: P
) => {
  const {
    internalType,
    variant,
    height,
    startIcon,
    endIcon,
    isAnchorOpen,
    textColor,
    ...props
  } = allProps
  return props
}
